import { useState, useRef, useEffect, useCallback } from "react";
import { SHIRT_FRONT_B64, SHIRT_BACK_B64 } from "./shirtImageData";

// ── Preload images once
const preloaded = {};
function loadImg(src) {
  if (preloaded[src]) return Promise.resolve(preloaded[src]);
  return new Promise(res => {
    const img = new Image();
    img.onload = () => { preloaded[src] = img; res(img); };
    img.src = src;
  });
}

function hexToRgb(hex) {
  const r = parseInt(hex.slice(1,3),16);
  const g = parseInt(hex.slice(3,5),16);
  const b = parseInt(hex.slice(5,7),16);
  return [r, g, b];
}

function drawPattern(ctx, patternId, color, width, height) {
  if (patternId === "none") return;
  const [r,g,b] = hexToRgb(color);
  const dark  = `rgba(${Math.round(r*0.55)},${Math.round(g*0.55)},${Math.round(b*0.55)},0.85)`;
  const light = `rgba(${Math.min(255,Math.round(r*1.15))},${Math.min(255,Math.round(g*1.15))},${Math.min(255,Math.round(b*1.15))},0.85)`;
  const p = ctx.createPattern((() => {
    const pc = document.createElement("canvas");
    const s = 28; pc.width = s; pc.height = s;
    const px = pc.getContext("2d");
    px.clearRect(0,0,s,s);
    switch(patternId) {
      case "stripes_h": px.fillStyle=dark;px.fillRect(0,0,s,s/2);px.fillStyle=light;px.fillRect(0,s/2,s,s/2);break;
      case "stripes_v": px.fillStyle=dark;px.fillRect(0,0,s/2,s);px.fillStyle=light;px.fillRect(s/2,0,s/2,s);break;
      case "dots": px.fillStyle=dark;px.beginPath();px.arc(s/2,s/2,s/4,0,Math.PI*2);px.fill();break;
      case "grid": px.strokeStyle=dark;px.lineWidth=2;px.beginPath();px.moveTo(0,0);px.lineTo(0,s);px.moveTo(0,0);px.lineTo(s,0);px.stroke();break;
      case "chevron": px.strokeStyle=dark;px.lineWidth=2.5;px.lineJoin="round";px.beginPath();px.moveTo(0,s*0.7);px.lineTo(s/2,s*0.2);px.lineTo(s,s*0.7);px.stroke();px.beginPath();px.moveTo(0,s*1.2);px.lineTo(s/2,s*0.7);px.lineTo(s,s*1.2);px.stroke();break;
      case "diagonal": px.strokeStyle=dark;px.lineWidth=3;for(let i=-s;i<s*2;i+=s/2){px.beginPath();px.moveTo(i,0);px.lineTo(i+s,s);px.stroke();}break;
      case "houndstooth":
        px.fillStyle=dark;px.fillRect(0,0,s/2,s/2);px.fillRect(s/2,s/2,s/2,s/2);
        px.beginPath();px.moveTo(s/2,0);px.lineTo(s,0);px.lineTo(s,s/4);px.lineTo(s*3/4,s/4);px.lineTo(s*3/4,s/2);px.lineTo(s/2,s/2);px.closePath();px.fill();
        px.beginPath();px.moveTo(0,s/2);px.lineTo(s/4,s/2);px.lineTo(s/4,s*3/4);px.lineTo(s/2,s*3/4);px.lineTo(s/2,s);px.lineTo(0,s);px.closePath();px.fill();
        break;
      default: break;
    }
    return pc;
  })(), "repeat");
  if (p) { ctx.fillStyle = p; ctx.fillRect(0,0,width,height); }
}

// ── CANVAS SHIRT RENDERER ─────────────────────────────────────────────────────
// designPos: {x, y} in canvas coords (center of design area)
// logoScale: 0.5–2.0 multiplier
function ShirtCanvas({ colorHex, patternId, text, textColor, fontSize, fontFamily,
                       logoSrc, logoImg, view, designPos, logoScale,
                       onDragStart, canvasRef: externalRef }) {
  const internalRef = useRef(null);
  const canvasRef = externalRef || internalRef;
  const W = 440, H = 563;

  const draw = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0,0,W,H);

    const shirtSrc = view === "front" ? SHIRT_FRONT_B64 : SHIRT_BACK_B64;
    const shirtImg = await loadImg(shirtSrc);
    const [r,g,b] = hexToRgb(colorHex);

    // 1. Fill shirt color masked to shirt shape
    ctx.save();
    ctx.drawImage(shirtImg, 0,0,W,H);
    const maskData = ctx.getImageData(0,0,W,H);
    ctx.clearRect(0,0,W,H);
    const colorData = ctx.createImageData(W,H);
    for (let i=0;i<maskData.data.length;i+=4) {
      if (maskData.data[i+3] > 10) {
        colorData.data[i]=r; colorData.data[i+1]=g; colorData.data[i+2]=b; colorData.data[i+3]=255;
      }
    }
    ctx.putImageData(colorData,0,0);
    ctx.restore();

    // 2. Pattern overlay
    if (patternId !== "none") {
      const patCanvas = document.createElement("canvas");
      patCanvas.width=W; patCanvas.height=H;
      const patCtx = patCanvas.getContext("2d");
      drawPattern(patCtx, patternId, colorHex, W, H);
      const patData = patCtx.getImageData(0,0,W,H);
      const current = ctx.getImageData(0,0,W,H);
      for (let i=0;i<maskData.data.length;i+=4) {
        if (maskData.data[i+3] > 10) {
          const pr=patData.data[i]/255, pg=patData.data[i+1]/255, pb=patData.data[i+2]/255, pa=patData.data[i+3]/255;
          if (pa>0.01) {
            const blend=0.5*pa;
            current.data[i]  =Math.round(current.data[i]  *(1-blend)+current.data[i]  *pr*255*blend/255);
            current.data[i+1]=Math.round(current.data[i+1]*(1-blend)+current.data[i+1]*pg*255*blend/255);
            current.data[i+2]=Math.round(current.data[i+2]*(1-blend)+current.data[i+2]*pb*255*blend/255);
          }
        }
      }
      ctx.putImageData(current,0,0);
    }

    // 3. Multiply shirt lighting (folds/shadows)
    ctx.save();
    ctx.globalCompositeOperation = "multiply";
    ctx.drawImage(shirtImg, 0,0,W,H);
    ctx.restore();

    // 4. Draw logo + text at designPos — ONLY for current view (front/back independent)
    const cx = designPos.x;
    const cy = designPos.y;
    const baseAreaW = W * 0.36 * logoScale;

    ctx.save();
    let textY = cy;

    if (logoImg) {
      const aspectRatio = logoImg.width / logoImg.height;
      const lw = baseAreaW;
      const lh = lw / aspectRatio;
      const lx = cx - lw / 2;
      const ly = cy - lh / 2;
      ctx.drawImage(logoImg, lx, ly, lw, lh);
      textY = ly + lh + (text ? fontSize * 0.8 : 0);
    }

    if (text) {
      ctx.font = `900 ${fontSize}px ${fontFamily}`;
      ctx.textAlign = "center";
      ctx.fillStyle = textColor;
      ctx.shadowColor = "rgba(0,0,0,0.45)";
      ctx.shadowBlur = 5;
      ctx.shadowOffsetY = 2;
      const words = text.split(" ");
      let line = "", lineY = logoImg ? textY : cy;
      for (const word of words) {
        const test = line ? line+" "+word : word;
        if (ctx.measureText(test).width > baseAreaW * 1.2 && line) {
          ctx.fillText(line, cx, lineY);
          line = word; lineY += fontSize * 1.25;
        } else { line = test; }
      }
      ctx.fillText(line, cx, lineY);
    }
    ctx.restore();

    // 5. Drag handle indicator (subtle crosshair at design center)
    ctx.save();
    ctx.strokeStyle = "rgba(255,255,255,0.25)";
    ctx.lineWidth = 1;
    ctx.setLineDash([3,4]);
    ctx.beginPath();
    ctx.arc(cx, cy, 18, 0, Math.PI*2);
    ctx.stroke();
    ctx.restore();

  }, [colorHex, patternId, text, textColor, fontSize, fontFamily, logoSrc, logoImg, view, designPos, logoScale]);

  useEffect(() => { draw(); }, [draw]);

  return (
    <canvas
      ref={canvasRef}
      width={W} height={H}
      style={{ width:"100%", maxWidth:440, display:"block", margin:"0 auto",
               cursor:"grab", filter:"drop-shadow(0 24px 60px rgba(0,0,0,0.6))" }}
      onMouseDown={onDragStart}
      onTouchStart={onDragStart}
    />
  );
}

// ── DATA ──────────────────────────────────────────────────────────────────────
const COLORS = [
  { name:"Arctic White",  hex:"#F5F5F0" },
  { name:"Jet Black",     hex:"#111111" },
  { name:"Navy",          hex:"#1B2A4A" },
  { name:"Crimson",       hex:"#B91C1C" },
  { name:"Forest",        hex:"#166534" },
  { name:"Royal Blue",    hex:"#1D4ED8" },
  { name:"Slate",         hex:"#4B5563" },
  { name:"Sand",          hex:"#B45309" },
  { name:"Burnt Orange",  hex:"#C2410C" },
  { name:"Plum",          hex:"#7E22CE" },
  { name:"Teal",          hex:"#0F766E" },
  { name:"Hot Pink",      hex:"#BE185D" },
];

const PATTERNS = [
  { id:"none",        label:"None",        icon:"○" },
  { id:"stripes_h",   label:"H-Stripe",    icon:"≡" },
  { id:"stripes_v",   label:"V-Stripe",    icon:"|||" },
  { id:"dots",        label:"Dots",        icon:"⁘" },
  { id:"grid",        label:"Grid",        icon:"⊞" },
  { id:"chevron",     label:"Chevron",     icon:"⌄⌄" },
  { id:"diagonal",    label:"Diagonal",    icon:"╲╲" },
  { id:"houndstooth", label:"Hound",       icon:"◆" },
];

const FONTS = ["Impact","Arial Black","Georgia","Courier New","Palatino Linotype","Trebuchet MS","Bebas Neue"];
const SIZES = ["XS","S","M","L","XL","2XL","3XL"];
const TIERS = [
  { min:1,   max:19,       price:18.99, label:"Retail",    badge:null        },
  { min:20,  max:49,       price:14.99, label:"Volume",    badge:"SAVE 21%"  },
  { min:50,  max:99,       price:11.99, label:"Wholesale", badge:"SAVE 37%"  },
  { min:100, max:Infinity, price:8.99,  label:"Bulk",      badge:"SAVE 53%"  },
];

// Default design positions for front/back (canvas coords 440×563)
const DEFAULT_POS = {
  front: { x: 220, y: 240 },
  back:  { x: 220, y: 230 },
};

// ── STYLE TOKENS ──────────────────────────────────────────────────────────────
const accent = "#FF6B35";
const accentAlt = "#FF3366";
const surface = "rgba(255,255,255,0.04)";
const border = "rgba(255,255,255,0.08)";

const styles = {
  card: {
    background: surface,
    border: `1px solid ${border}`,
    borderRadius: 20,
    padding: "20px 22px",
    marginBottom: 14,
  },
  label: {
    fontSize: 9,
    color: "#666",
    letterSpacing: "2px",
    textTransform: "uppercase",
    marginBottom: 8,
    fontWeight: 800,
    display: "block",
  },
  pill: (active) => ({
    padding: "8px 14px",
    borderRadius: 10,
    border: `1px solid ${active ? accent : "rgba(255,255,255,0.1)"}`,
    background: active ? `${accent}20` : "rgba(255,255,255,0.03)",
    color: active ? accent : "#666",
    cursor: "pointer",
    fontFamily: "inherit",
    fontWeight: 700,
    fontSize: 10,
    letterSpacing: "1px",
    textTransform: "uppercase",
    transition: "all 0.15s",
  }),
};

function Card({ title, icon, children }) {
  return (
    <div style={styles.card}>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "2px",
                    textTransform: "uppercase", color: accent, marginBottom: 16,
                    display: "flex", alignItems: "center", gap: 6 }}>
        {icon && <span>{icon}</span>}
        {title}
      </div>
      {children}
    </div>
  );
}

function RangeInput({ label, value, min, max, step=1, onChange, unit="" }) {
  return (
    <div style={{ marginBottom: 4 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
        <span style={styles.label}>{label}</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: accent }}>{value}{unit}</span>
      </div>
      <div style={{ position: "relative" }}>
        <input type="range" min={min} max={max} step={step} value={value} onChange={e=>onChange(+e.target.value)}
          style={{ width: "100%", accentColor: accent, height: 4 }}/>
      </div>
    </div>
  );
}

// ── MAIN APP ──────────────────────────────────────────────────────────────────
export default function TShirtDesigner() {
  const [tab, setTab]           = useState("design");
  const [view, setView]         = useState("front");
  const [color, setColor]       = useState(COLORS[2]);
  const [pattern, setPattern]   = useState("none");

  // Separate text/logo state per view
  const [frontText, setFrontText]       = useState("YOUR BRAND");
  const [backText,  setBackText]        = useState("");
  const [frontTextColor, setFrontTextColor] = useState("#FFFFFF");
  const [backTextColor,  setBackTextColor]  = useState("#FFFFFF");
  const [fontSize, setFontSize]         = useState(30);
  const [fontFamily, setFont]           = useState("Impact");

  // Separate logo state per view — front logo never bleeds to back
  const [frontLogoSrc, setFrontLogoSrc] = useState(null);
  const [frontLogoImg, setFrontLogoImg] = useState(null);
  const [backLogoSrc,  setBackLogoSrc]  = useState(null);
  const [backLogoImg,  setBackLogoImg]  = useState(null);

  // Logo scale (shared, or can be split if needed)
  const [frontLogoScale, setFrontLogoScale] = useState(1.0);
  const [backLogoScale,  setBackLogoScale]  = useState(1.0);

  // Draggable design position per view (canvas coords)
  const [designPos, setDesignPos] = useState({ ...DEFAULT_POS });

  const [quantities, setQty]    = useState({ S:0, M:10, L:5, XL:2, "2XL":0 });
  const [ordered, setOrdered]   = useState(false);

  const frontFileRef = useRef();
  const backFileRef  = useRef();
  const canvasRef    = useRef();
  const dragState    = useRef(null);

  // ── Derived
  const totalQty = Object.values(quantities).reduce((a,b)=>a+b,0);
  const tier = TIERS.find(t=>totalQty>=t.min&&totalQty<=t.max)||TIERS[0];
  const total = (totalQty * tier.price).toFixed(2);

  const text      = view === "front" ? frontText      : backText;
  const setText   = view === "front" ? setFrontText   : setBackText;
  const textColor = view === "front" ? frontTextColor : backTextColor;
  const setTextColor = view === "front" ? setFrontTextColor : setBackTextColor;
  const logoSrc   = view === "front" ? frontLogoSrc  : backLogoSrc;
  const logoImg   = view === "front" ? frontLogoImg  : backLogoImg;
  const logoScale = view === "front" ? frontLogoScale : backLogoScale;
  const setLogoScale = view === "front" ? setFrontLogoScale : setBackLogoScale;
  const fileRef   = view === "front" ? frontFileRef  : backFileRef;

  const handleUpload = (e) => {
    const f = e.target.files[0]; if (!f) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const src = ev.target.result;
      const img = new Image();
      img.onload = () => {
        if (view === "front") { setFrontLogoSrc(src); setFrontLogoImg(img); }
        else                  { setBackLogoSrc(src);  setBackLogoImg(img);  }
      };
      img.src = src;
    };
    reader.readAsDataURL(f);
  };

  const removelogo = () => {
    if (view === "front") { setFrontLogoSrc(null); setFrontLogoImg(null); }
    else                  { setBackLogoSrc(null);  setBackLogoImg(null);  }
  };

  const adjQty = (sz, d) => setQty(q => ({ ...q, [sz]: Math.max(0,(q[sz]||0)+d) }));

  // ── Drag logic: map mouse/touch coords → canvas coords (440×563 space)
  const getCanvasXY = useCallback((clientX, clientY) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const scaleX = 440 / rect.width;
    const scaleY = 563 / rect.height;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top)  * scaleY,
    };
  }, []);

  const handleDragStart = useCallback((e) => {
    e.preventDefault();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const pos = getCanvasXY(clientX, clientY);
    if (!pos) return;
    dragState.current = { startX: pos.x, startY: pos.y,
      origX: designPos[view].x, origY: designPos[view].y };

    const onMove = (ev) => {
      const cx = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const cy = ev.touches ? ev.touches[0].clientY : ev.clientY;
      const cur = getCanvasXY(cx, cy);
      if (!cur || !dragState.current) return;
      const dx = cur.x - dragState.current.startX;
      const dy = cur.y - dragState.current.startY;
      setDesignPos(prev => ({
        ...prev,
        [view]: {
          x: Math.max(60, Math.min(380, dragState.current.origX + dx)),
          y: Math.max(80, Math.min(500, dragState.current.origY + dy)),
        }
      }));
    };

    const onUp = () => {
      dragState.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup",   onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend",  onUp);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup",   onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend",  onUp);
  }, [designPos, view, getCanvasXY]);

  const resetPos = () => setDesignPos(prev => ({ ...prev, [view]: { ...DEFAULT_POS[view] } }));

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(160deg,#07070f 0%,#0d0d1c 55%,#07070f 100%)",
      fontFamily: "'Trebuchet MS','Lucida Grande',sans-serif",
      color: "#E8E6E1",
    }}>

      {/* ── HEADER ── */}
      <header style={{
        position: "sticky", top: 0, zIndex: 200,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 32px", height: 64,
        background: "rgba(7,7,15,0.92)",
        backdropFilter: "blur(28px)",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:14 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 12,
            background: `linear-gradient(135deg,${accent},${accentAlt})`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 20, fontWeight: 900, boxShadow: `0 4px 20px ${accent}55`,
          }}>✦</div>
          <div>
            <div style={{ fontWeight: 900, fontSize: 18, letterSpacing: "-0.5px", lineHeight: 1 }}>IESGEAR</div>
            <div style={{ fontSize: 9, color: "#555", letterSpacing: "3px", textTransform: "uppercase" }}>CUSTOM APPAREL</div>
          </div>
        </div>
        <nav style={{ display: "flex", gap: 4 }}>
          {[["design","🎨 Design"],["order","📦 Order"],["gallery","🖼 Samples"]].map(([id,label])=>(
            <button key={id} onClick={()=>setTab(id)} style={{
              ...styles.pill(tab===id),
              padding: "9px 18px",
            }}>{label}</button>
          ))}
        </nav>
      </header>

      <div style={{ maxWidth: 1240, margin: "0 auto", padding: "32px 24px" }}>

        {/* ══ DESIGN TAB ══ */}
        {tab === "design" && (
          <div style={{ display:"grid", gridTemplateColumns:"1fr 380px", gap:32, alignItems:"start" }}>

            {/* ── PREVIEW PANEL ── */}
            <div style={{ position:"sticky", top:80 }}>
              <div style={{
                background: "rgba(255,255,255,0.025)",
                border: `1px solid ${border}`,
                borderRadius: 28,
                padding: 32,
              }}>
                {/* View toggle + title */}
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:24 }}>
                  <div>
                    <div style={{ fontSize:22, fontWeight:900, letterSpacing:"-0.5px" }}>Live Preview</div>
                    <div style={{ fontSize:11, color:"#555", marginTop:4 }}>
                      Drag design to reposition · {view === "front" ? "Front view" : "Back view"}
                    </div>
                  </div>
                  <div style={{ display:"flex", gap:8 }}>
                    {["front","back"].map(v=>(
                      <button key={v} onClick={()=>setView(v)} style={{
                        ...styles.pill(view===v),
                        padding:"10px 20px",
                      }}>{v==="front"?"◉ Front":"◎ Back"}</button>
                    ))}
                  </div>
                </div>

                {/* Canvas area */}
                <div style={{
                  background:`radial-gradient(ellipse at 50% 45%,${color.hex}22 0%,transparent 68%)`,
                  borderRadius: 20, padding: "24px 28px",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  minHeight: 400,
                }}>
                  <ShirtCanvas
                    colorHex={color.hex} patternId={pattern}
                    text={text} textColor={textColor}
                    fontSize={fontSize} fontFamily={fontFamily}
                    logoSrc={logoSrc} logoImg={logoImg}
                    logoScale={logoScale}
                    view={view}
                    designPos={designPos[view]}
                    onDragStart={handleDragStart}
                    canvasRef={canvasRef}
                  />
                </div>

                {/* Drag hint + reset */}
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:14, marginBottom:16 }}>
                  <div style={{ fontSize:11, color:"#444" }}>
                    ↕ Drag on shirt to reposition design
                  </div>
                  <button onClick={resetPos} style={{
                    fontSize:10, color:"#555", background:"transparent",
                    border:"1px solid rgba(255,255,255,0.08)", borderRadius:8,
                    padding:"5px 12px", cursor:"pointer", fontFamily:"inherit", fontWeight:700,
                  }}>Reset position</button>
                </div>

                {/* Info chips */}
                <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginBottom:20 }}>
                  {[
                    { k:"Color",   v:color.name },
                    { k:"Pattern", v:PATTERNS.find(p=>p.id===pattern)?.label },
                    { k:"Font",    v:fontFamily.split(" ")[0] },
                  ].map(s=>(
                    <div key={s.k} style={{
                      background:"rgba(255,255,255,0.03)", borderRadius:12,
                      padding:"11px 14px", border:`1px solid ${border}`,
                    }}>
                      <div style={{ fontSize:9, color:"#555", letterSpacing:"1.5px", textTransform:"uppercase" }}>{s.k}</div>
                      <div style={{ fontSize:12, fontWeight:800, marginTop:4 }}>{s.v}</div>
                    </div>
                  ))}
                </div>

                <button onClick={()=>setTab("order")} style={{
                  width:"100%", padding:"16px", borderRadius:14, border:"none",
                  background:`linear-gradient(135deg,${accent},${accentAlt})`,
                  color:"#fff", fontFamily:"inherit", fontWeight:900, fontSize:15,
                  cursor:"pointer", boxShadow:`0 8px 32px ${accent}44`,
                  letterSpacing:"0.5px",
                }}>Proceed to Order →</button>
              </div>
            </div>

            {/* ── CONTROLS PANEL ── */}
            <div>

              {/* Shirt Color */}
              <Card title="Shirt Color" icon="🎨">
                <div style={{ display:"grid", gridTemplateColumns:"repeat(6,1fr)", gap:8 }}>
                  {COLORS.map(c=>(
                    <button key={c.hex} onClick={()=>setColor(c)} title={c.name} style={{
                      aspectRatio:"1", borderRadius:12,
                      border:`3px solid ${color.hex===c.hex ? accent : "transparent"}`,
                      background: c.hex, cursor:"pointer",
                      boxShadow: color.hex===c.hex ? `0 0 0 2px ${accent}` : "none",
                      transition:"all 0.15s",
                    }}/>
                  ))}
                </div>
                <div style={{ marginTop:12, fontSize:12, color:"#666", fontWeight:600 }}>
                  Selected: <span style={{ color:"#E8E6E1" }}>{color.name}</span>
                </div>
              </Card>

              {/* Pattern */}
              <Card title="Fabric Pattern" icon="🔷">
                <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:7 }}>
                  {PATTERNS.map(p=>(
                    <button key={p.id} onClick={()=>setPattern(p.id)} style={{
                      ...styles.pill(pattern===p.id),
                      padding:"10px 4px", display:"flex", flexDirection:"column",
                      alignItems:"center", gap:5,
                    }}>
                      <span style={{ fontSize:16 }}>{p.icon}</span>
                      <span style={{ fontSize:9 }}>{p.label}</span>
                    </button>
                  ))}
                </div>
              </Card>

              {/* Text */}
              <Card title={`Custom Text — ${view === "front" ? "Front" : "Back"}`} icon="✏️">
                <textarea value={text} onChange={e=>setText(e.target.value)}
                  placeholder="Brand name / slogan..." maxLength={60} rows={2}
                  style={{
                    width:"100%", background:"rgba(255,255,255,0.06)",
                    border:`1px solid ${border}`, borderRadius:10,
                    padding:"11px 14px", color:"#E8E6E1",
                    fontFamily:"inherit", fontSize:14, outline:"none",
                    resize:"none", boxSizing:"border-box", marginBottom:12,
                  }}/>

                <div style={{ display:"grid", gridTemplateColumns:"1fr 68px", gap:10, marginBottom:14 }}>
                  <div>
                    <span style={styles.label}>Font Family</span>
                    <select value={fontFamily} onChange={e=>setFont(e.target.value)} style={{
                      width:"100%", background:"rgba(255,255,255,0.07)",
                      border:`1px solid ${border}`, borderRadius:10,
                      padding:"9px 12px", color:"#E8E6E1",
                      fontFamily:"inherit", fontSize:12, outline:"none",
                    }}>
                      {FONTS.map(f=><option key={f} style={{ background:"#111" }}>{f}</option>)}
                    </select>
                  </div>
                  <div>
                    <span style={styles.label}>Color</span>
                    <input type="color" value={textColor} onChange={e=>setTextColor(e.target.value)} style={{
                      width:"100%", height:40, borderRadius:10,
                      border:`1px solid ${border}`, cursor:"pointer", background:"transparent",
                    }}/>
                  </div>
                </div>
                <RangeInput label="Text Size" value={fontSize} min={12} max={58} onChange={setFontSize} unit="px"/>
              </Card>

              {/* Logo */}
              <Card title={`Logo — ${view === "front" ? "Front" : "Back"}`} icon="🖼">
                <button onClick={()=>fileRef.current.click()} style={{
                  width:"100%", padding:"14px", borderRadius:12,
                  border:`2px dashed ${logoSrc ? accent+"66" : "rgba(255,255,255,0.12)"}`,
                  background: logoSrc ? `${accent}0A` : "rgba(255,255,255,0.02)",
                  color: logoSrc ? accent : "#555",
                  cursor:"pointer", fontFamily:"inherit", fontSize:12,
                  fontWeight:800, transition:"all 0.2s", marginBottom:10,
                }}>
                  {logoSrc ? "✅ Logo uploaded · click to replace" : "⬆ Upload PNG / SVG / JPG logo"}
                </button>
                <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} style={{ display:"none" }}/>

                {logoSrc && (
                  <div>
                    <div style={{ display:"flex", gap:10, marginBottom:12 }}>
                      <img src={logoSrc} alt="" style={{
                        width:56, height:56, objectFit:"contain",
                        background:"rgba(255,255,255,0.06)", borderRadius:10, padding:5,
                      }}/>
                      <button onClick={removelogo} style={{
                        flex:1, borderRadius:10, border:"1px solid rgba(239,68,68,0.3)",
                        background:"rgba(239,68,68,0.08)", color:"#EF4444",
                        cursor:"pointer", fontFamily:"inherit", fontWeight:800, fontSize:11,
                      }}>✕ Remove</button>
                    </div>
                    <RangeInput label="Logo Size" value={Math.round(logoScale*100)} min={30} max={200}
                      onChange={v=>setLogoScale(v/100)} unit="%"/>
                  </div>
                )}

                <div style={{ marginTop:10, padding:"10px 12px", borderRadius:10,
                  background:"rgba(255,255,255,0.03)", border:`1px solid ${border}`,
                  fontSize:11, color:"#555", lineHeight:1.6 }}>
                  💡 Front and back logos are independent — switching views won't share them.
                </div>
              </Card>

            </div>
          </div>
        )}

        {/* ══ ORDER TAB ══ */}
        {tab === "order" && (
          <div style={{ maxWidth:700, margin:"0 auto" }}>
            <div style={{ textAlign:"center", marginBottom:40 }}>
              <div style={{ fontSize:34, fontWeight:900, letterSpacing:"-1px" }}>Place Your Order</div>
              <div style={{ color:"#555", marginTop:10, fontSize:14 }}>Bulk discounts applied automatically</div>
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:30 }}>
              {TIERS.map(t=>{
                const act = tier.label===t.label;
                return (
                  <div key={t.label} style={{
                    borderRadius:18, padding:"18px 12px", textAlign:"center",
                    border:`1px solid ${act ? accent : border}`,
                    background: act ? `${accent}12` : surface,
                    transition:"all 0.2s",
                  }}>
                    {t.badge&&<div style={{
                      fontSize:8, background:accent, color:"#fff", borderRadius:4,
                      padding:"2px 7px", display:"inline-block", fontWeight:900,
                      letterSpacing:"1px", marginBottom:8,
                    }}>{t.badge}</div>}
                    <div style={{ fontSize:10, color:act?accent:"#555", fontWeight:800, letterSpacing:"1px", textTransform:"uppercase" }}>{t.label}</div>
                    <div style={{ fontSize:26, fontWeight:900, margin:"6px 0", color:act?accent:"#E8E6E1" }}>${t.price}</div>
                    <div style={{ fontSize:10, color:"#444" }}>{t.min===100?"100+ pcs":`${t.min}–${t.max} pcs`}</div>
                    {act&&<div style={{ fontSize:10, color:accent, fontWeight:800, marginTop:5 }}>ACTIVE ✓</div>}
                  </div>
                );
              })}
            </div>

            <div style={{ ...styles.card, marginBottom:20 }}>
              <div style={{ fontWeight:800, fontSize:15, marginBottom:18 }}>📏 Size Breakdown</div>
              {SIZES.map(sz=>(
                <div key={sz} style={{
                  display:"flex", alignItems:"center", justifyContent:"space-between",
                  padding:"12px 16px", marginBottom:8,
                  background:"rgba(255,255,255,0.03)", borderRadius:12,
                  border:`1px solid ${border}`,
                }}>
                  <span style={{ fontWeight:900, fontSize:15, width:44 }}>{sz}</span>
                  <div style={{ display:"flex", alignItems:"center", gap:14 }}>
                    <button onClick={()=>adjQty(sz,-1)} style={{
                      width:34, height:34, borderRadius:9,
                      border:"1px solid rgba(255,255,255,0.12)",
                      background:"rgba(255,255,255,0.05)", color:"#fff",
                      cursor:"pointer", fontSize:18, fontWeight:700,
                    }}>−</button>
                    <span style={{ width:36, textAlign:"center", fontWeight:900, fontSize:16 }}>{quantities[sz]||0}</span>
                    <button onClick={()=>adjQty(sz,1)} style={{
                      width:34, height:34, borderRadius:9,
                      border:`1px solid ${accent}44`, background:`${accent}18`,
                      color:accent, cursor:"pointer", fontSize:18, fontWeight:700,
                    }}>+</button>
                  </div>
                  <span style={{ color:"#444", fontSize:13, width:80, textAlign:"right" }}>
                    {(quantities[sz]||0)>0 ? `$${((quantities[sz]||0)*tier.price).toFixed(2)}` : "—"}
                  </span>
                </div>
              ))}
            </div>

            <div style={{
              background:`linear-gradient(135deg,${accent}10,${accentAlt}08)`,
              borderRadius:20, border:`1px solid ${accent}30`,
              padding:24, marginBottom:20,
            }}>
              {[{l:"Total Pieces",v:`${totalQty} pcs`},{l:"Price / Unit",v:`$${tier.price}`},{l:"Tier",v:tier.label}].map(r=>(
                <div key={r.l} style={{ display:"flex", justifyContent:"space-between", marginBottom:11 }}>
                  <span style={{ color:"#666" }}>{r.l}</span>
                  <span style={{ fontWeight:700, color:accent }}>{r.v}</span>
                </div>
              ))}
              <div style={{ height:1, background:`${accent}25`, margin:"16px 0" }}/>
              <div style={{ display:"flex", justifyContent:"space-between" }}>
                <span style={{ fontWeight:900, fontSize:17 }}>Order Total</span>
                <span style={{ fontWeight:900, fontSize:30, color:accent }}>${total}</span>
              </div>
            </div>

            {!ordered ? (
              <button onClick={()=>{ if(totalQty>0) setOrdered(true); }} disabled={totalQty===0} style={{
                width:"100%", padding:"18px", borderRadius:16, border:"none",
                background: totalQty>0 ? `linear-gradient(135deg,${accent},${accentAlt})` : "#1a1a1a",
                color: totalQty>0 ? "#fff" : "#333",
                fontFamily:"inherit", fontWeight:900, fontSize:16,
                cursor: totalQty>0 ? "pointer" : "not-allowed",
                boxShadow: totalQty>0 ? `0 10px 36px ${accent}44` : "none",
                letterSpacing:"0.3px",
              }}>
                {totalQty===0 ? "Select at least 1 shirt" : `Confirm Order — ${totalQty} shirts · $${total}`}
              </button>
            ) : (
              <div style={{
                textAlign:"center", padding:48,
                background:"rgba(34,197,94,0.06)", borderRadius:22,
                border:"1px solid rgba(34,197,94,0.18)",
              }}>
                <div style={{ fontSize:56 }}>🎉</div>
                <div style={{ fontWeight:900, fontSize:24, color:"#22C55E", marginBottom:8, marginTop:12 }}>Order Confirmed!</div>
                <div style={{ color:"#555", fontSize:13 }}>{totalQty} shirts · ${total} · Confirmation sent to your email</div>
                <button onClick={()=>{ setOrdered(false); setTab("design"); }} style={{
                  marginTop:22, padding:"13px 28px", borderRadius:12,
                  background:"rgba(255,255,255,0.07)", border:`1px solid ${border}`,
                  color:"#fff", fontFamily:"inherit", fontWeight:700, cursor:"pointer", fontSize:13,
                }}>← Design Another</button>
              </div>
            )}
          </div>
        )}

        {/* ══ GALLERY TAB ══ */}
        {tab === "gallery" && (
          <div>
            <div style={{ textAlign:"center", marginBottom:40 }}>
              <div style={{ fontSize:34, fontWeight:900, letterSpacing:"-1px" }}>Sample Designs</div>
              <div style={{ color:"#555", marginTop:10, fontSize:14 }}>Click any design to apply it instantly</div>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:20 }}>
              {[
                { color:COLORS[2], pat:"none",       text:"SQUAD",   tc:"#fff", font:"Impact",      lbl:"Classic Navy"    },
                { color:COLORS[3], pat:"stripes_h",  text:"HUSTLE",  tc:"#fff", font:"Arial Black",  lbl:"Crimson Stripes" },
                { color:COLORS[4], pat:"none",       text:"UNITY",   tc:"#fff", font:"Georgia",      lbl:"Forest Solid"    },
                { color:COLORS[5], pat:"dots",       text:"CREW",    tc:"#fff", font:"Impact",       lbl:"Royal Dots"      },
                { color:COLORS[8], pat:"chevron",    text:"GRIND",   tc:"#fff", font:"Arial Black",  lbl:"Orange Chevron"  },
                { color:COLORS[0], pat:"grid",       text:"LEGENDS", tc:"#111", font:"Impact",       lbl:"White Grid"      },
              ].map((item,i)=>(
                <div key={i} onClick={()=>{
                  setColor(item.color); setPattern(item.pat);
                  setFrontText(item.text); setFrontTextColor(item.tc);
                  setFont(item.font); setTab("design"); setView("front");
                }} style={{
                  background: surface, border:`1px solid ${border}`,
                  borderRadius:20, padding:"22px 18px",
                  cursor:"pointer", transition:"all 0.2s",
                }}>
                  <div style={{ width:"85%", margin:"0 auto 16px" }}>
                    <ShirtCanvas
                      colorHex={item.color.hex} patternId={item.pat}
                      text={item.text} textColor={item.tc}
                      fontSize={26} fontFamily={item.font}
                      logoSrc={null} logoImg={null} logoScale={1}
                      view="front"
                      designPos={DEFAULT_POS.front}
                      onDragStart={()=>{}}
                    />
                  </div>
                  <div style={{ textAlign:"center" }}>
                    <div style={{ fontWeight:800, fontSize:13 }}>{item.lbl}</div>
                    <div style={{ fontSize:11, color:"#444", marginTop:5 }}>Click to apply</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
